import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth, useLang } from "../../lib/ctx";
import { Logo } from "../../components/Logo";
import { PhoneLogin } from "../../components/store/PhoneLogin";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Shell = ({ title, children }) => (
  <div className="mx-auto max-w-md px-4 py-20">
    <Logo />
    <h1 className="mt-10 font-display text-5xl">{title}</h1>
    <div className="mt-8">{children}</div>
  </div>
);

export function AuthPage({ mode }) {
  const { t, lang } = useLang();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/${mode}`, mode === "login" ? { email: f.email.trim(), password: f.password } : { ...f, email: f.email.trim() });
      setUser(data);
      nav(["admin", "staff"].includes(data.role) ? "/admin" : "/account");
    } catch (x) { toast.error(errMsg(x)); }
    setBusy(false);
  };
  return (
    <Shell title={mode === "login" ? t("login") : t("register")}>
      <form onSubmit={submit} className="space-y-3" data-testid={`${mode}-form`}>
        {mode === "register" && <input required placeholder={t("fullName")} value={f.name} onChange={s("name")} className="field" data-testid="register-name-input" />}
        <input required type="email" autoComplete="username" autoCapitalize="none" placeholder={t("email")} value={f.email} onChange={s("email")} className="field" data-testid={`${mode}-email-input`} />
        {mode === "register" && <input placeholder={t("phone")} value={f.phone} onChange={s("phone")} className="field" data-testid="register-phone-input" />}
        <input required type={showPw ? "text" : "password"} autoComplete={mode === "register" ? "new-password" : "current-password"} minLength={mode === "register" ? 8 : 1} placeholder={t("password")} value={f.password} onChange={s("password")} className="field" data-testid={`${mode}-password-input`} />
        <label className="flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} className="accent-[#C8A45D]" data-testid="show-password-checkbox" />{lang === "bm" ? "Tunjuk kata laluan" : "Show password"}</label>
        {mode === "register" && <label className="flex gap-2 text-xs text-zinc-400"><input type="checkbox" checked={f.marketing_consent} onChange={s("marketing_consent")} className="accent-[#C8A45D]" data-testid="register-consent-checkbox" />{t("consent")}</label>}
        <button disabled={busy} className="btn-gold w-full" data-testid={`${mode}-submit-btn`}>{busy ? "…" : mode === "login" ? t("login") : t("register")}</button>
      </form>
      <button onClick={googleLogin} className="mt-3 flex w-full items-center justify-center gap-3 rounded-sm border border-[#dadce0] bg-white px-4 py-3 text-sm font-semibold text-[#3c4043] transition-colors duration-200 hover:bg-[#f8f9fa]" data-testid="google-login-btn">
        <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" /><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" /><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" /><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" /></svg>
        {t("google")}
      </button>
      <PhoneLogin />
      <div className="mt-6 flex justify-between text-sm text-zinc-400">
        {mode === "login" ? <Link to="/register" data-testid="go-register-link">{t("noAccount")} <span className="text-[#C8A45D]">{t("register")}</span></Link> : <Link to="/login" data-testid="go-login-link">{t("haveAccount")} <span className="text-[#C8A45D]">{t("login")}</span></Link>}
        {mode === "login" && <Link to="/forgot-password" className="hover:text-white" data-testid="forgot-link">{t("forgot")}</Link>}
      </div>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), origin_url: window.location.origin });
      toast.success("If that email exists, a reset link has been sent.");
    } catch (x) { toast.error(errMsg(x)); }
  };
  return <Shell title="Reset password"><form onSubmit={submit} className="space-y-3"><input required type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="field" data-testid="forgot-email-input" /><button className="btn-gold w-full" data-testid="forgot-submit-btn">Send link</button></form></Shell>;
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success("Password updated"); nav("/login"); } catch (x) { toast.error(errMsg(x)); }
  };
  return <Shell title="New password"><form onSubmit={submit} className="space-y-3"><input required minLength={8} type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className="field" data-testid="reset-password-input" /><button className="btn-gold w-full" data-testid="reset-submit-btn">Save</button></form></Shell>;
}

export function AuthCallback() {
  const { hash } = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid }).then(({ data }) => {
      setUser(data);
      window.history.replaceState(null, "", window.location.pathname);
      nav(["admin", "staff"].includes(data.role) ? "/admin" : "/account", { replace: true, state: { user: data } });
    }).catch(() => nav("/login", { replace: true }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
